import React from 'react';

/**
 * Premium orbital hero visual — pure CSS/SVG, no external deps.
 * Centered gold core surrounded by tilted concentric rings with orbiting dots.
 * Designed for the Light Powder-Blue + Gold theme.
 */
const HeroVisual = ({ animate = true }) => {
  return (
    <div
      className={`hero-visual ${animate ? 'is-animated' : ''}`}
      role="img"
      aria-label="Innovation orbits — VDM Business Innovations"
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'visible',
      }}
    >
      {/* Soft ambient backglow */}
      <div className="hv-glow" aria-hidden="true" />

      {/* Tilted orbital rings */}
      <div className="hv-ring hv-ring--1" aria-hidden="true">
        <span className="hv-dot hv-dot--a" />
      </div>
      <div className="hv-ring hv-ring--2" aria-hidden="true">
        <span className="hv-dot hv-dot--b" />
      </div>
      <div className="hv-ring hv-ring--3" aria-hidden="true">
        <span className="hv-dot hv-dot--c" />
        <span className="hv-dot hv-dot--c2" />
      </div>

      {/* Concentric guide rings (static) */}
      <div className="hv-guide hv-guide--1" aria-hidden="true" />
      <div className="hv-guide hv-guide--2" aria-hidden="true" />

      {/* Central core */}
      <div className="hv-core" aria-hidden="true">
        <div className="hv-core-inner" />
        <div className="hv-core-shine" />
      </div>

      {/* Floating accent particles */}
      <span className="hv-particle hv-particle--1" aria-hidden="true" />
      <span className="hv-particle hv-particle--2" aria-hidden="true" />
      <span className="hv-particle hv-particle--3" aria-hidden="true" />
      <span className="hv-particle hv-particle--4" aria-hidden="true" />

      <style>{`
        .hero-visual {
          --gold: #B8860B;
          --gold-soft: rgba(184, 134, 11, 0.55);
          --gold-faint: rgba(184, 134, 11, 0.18);
          --navy-faint: rgba(14, 42, 71, 0.18);
          --aspect: min(560px, 100%);
        }

        /* Entrance reveal — staggered fade + scale-in for each layer */
        .hero-visual .hv-glow,
        .hero-visual .hv-guide,
        .hero-visual .hv-ring,
        .hero-visual .hv-core,
        .hero-visual .hv-particle {
          opacity: 0;
          transition: opacity 1100ms cubic-bezier(0.22, 1, 0.36, 1);
          will-change: opacity, transform;
        }

        /* Mobile: clip decorative rings so they don't cause horizontal scroll */
        @media (max-width: 768px) {
          .hero-visual {
            overflow: hidden !important;
          }
        }
        .hero-visual .hv-ring,
        .hero-visual .hv-guide {
          /* keep base transform from existing rules; layer entrance via scale on a wrapper not needed */
        }
        .hero-visual.is-animated .hv-glow      { opacity: 0.95; transition-delay: 200ms; }
        .hero-visual.is-animated .hv-guide--2  { opacity: 0.5;  transition-delay: 380ms; }
        .hero-visual.is-animated .hv-guide--1  { opacity: 1;    transition-delay: 480ms; }
        .hero-visual.is-animated .hv-core      { opacity: 1;    transition-delay: 600ms;
                                                  animation-delay: 600ms; }
        .hero-visual.is-animated .hv-ring--3   { opacity: 1;    transition-delay: 720ms;
                                                  animation-delay: 720ms; }
        .hero-visual.is-animated .hv-ring--2   { opacity: 1;    transition-delay: 860ms;
                                                  animation-delay: 860ms; }
        .hero-visual.is-animated .hv-ring--1   { opacity: 1;    transition-delay: 1000ms;
                                                  animation-delay: 1000ms; }
        .hero-visual.is-animated .hv-particle--1 { opacity: 1;  transition-delay: 1150ms;
                                                   animation-delay: 1150ms; }
        .hero-visual.is-animated .hv-particle--2 { opacity: 0.8; transition-delay: 1250ms;
                                                   animation-delay: 1250ms; }
        .hero-visual.is-animated .hv-particle--3 { opacity: 0.7; transition-delay: 1350ms;
                                                   animation-delay: 1350ms; }
        .hero-visual.is-animated .hv-particle--4 { opacity: 0.6; transition-delay: 1450ms;
                                                   animation-delay: 1450ms; }

        .hv-glow {
          position: absolute;
          width: 80%;
          height: 80%;
          border-radius: 50%;
          background:
            radial-gradient(circle at 50% 50%, rgba(184, 134, 11, 0.28) 0%, rgba(184, 134, 11, 0.08) 35%, transparent 70%);
          filter: blur(20px);
          animation: hv-breath 7s ease-in-out infinite;
        }

        .hv-guide {
          position: absolute;
          border-radius: 50%;
          border: 1px dashed var(--navy-faint);
        }
        .hv-guide--1 { width: 78%; height: 78%; }
        .hv-guide--2 { width: 96%; height: 96%; opacity: 0.5; }

        .hv-ring {
          position: absolute;
          border-radius: 50%;
          border: 1.5px solid var(--gold-soft);
          box-shadow: 0 0 18px rgba(184, 134, 11, 0.18) inset;
        }
        .hv-ring--1 {
          width: 55%;
          height: 55%;
          transform: rotateX(70deg);
          animation: hv-spin 14s linear infinite;
        }
        .hv-ring--2 {
          width: 72%;
          height: 72%;
          transform: rotateX(60deg) rotateZ(45deg);
          animation: hv-spin 22s linear infinite reverse;
          border-color: rgba(184, 134, 11, 0.40);
        }
        .hv-ring--3 {
          width: 92%;
          height: 92%;
          transform: rotateX(72deg) rotateZ(-30deg);
          animation: hv-spin 32s linear infinite;
          border-color: rgba(184, 134, 11, 0.28);
        }

        .hv-dot {
          position: absolute;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: radial-gradient(circle at 30% 30%, #f3d27a 0%, var(--gold) 70%);
          box-shadow: 0 0 14px rgba(184, 134, 11, 0.85);
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
        .hv-dot--a { transform: translate(-50%, -50%) translateX(50%) translateY(-1px); top: 0; left: 50%; }
        .hv-dot--b { width: 10px; height: 10px; top: 50%; left: 100%; transform: translate(-50%, -50%); }
        .hv-dot--c { width: 8px; height: 8px; top: 0%; left: 50%; transform: translate(-50%, -50%); }
        .hv-dot--c2 { width: 6px; height: 6px; top: 100%; left: 50%; transform: translate(-50%, -50%); opacity: 0.7; }

        .hv-core {
          position: relative;
          width: 28%;
          aspect-ratio: 1 / 1;
          border-radius: 50%;
          background:
            radial-gradient(circle at 35% 30%, #fff1c2 0%, #e7b75b 28%, #B8860B 60%, #7a5807 100%);
          box-shadow:
            0 0 0 6px rgba(184, 134, 11, 0.10),
            0 0 0 14px rgba(184, 134, 11, 0.05),
            0 18px 60px rgba(184, 134, 11, 0.35),
            inset 0 -10px 30px rgba(0, 0, 0, 0.15);
          animation: hv-float 6s ease-in-out infinite;
        }
        .hv-core-inner {
          position: absolute;
          inset: 18%;
          border-radius: 50%;
          background: radial-gradient(circle at 45% 35%, rgba(255,255,255,0.55) 0%, transparent 55%);
          mix-blend-mode: screen;
        }
        .hv-core-shine {
          position: absolute;
          width: 30%;
          height: 18%;
          top: 16%;
          left: 22%;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.6);
          filter: blur(6px);
          transform: rotate(-20deg);
        }

        .hv-particle {
          position: absolute;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--gold);
          box-shadow: 0 0 10px rgba(184, 134, 11, 0.8);
        }
        .hv-particle--1 { top: 18%; left: 14%; animation: hv-drift 9s ease-in-out infinite; }
        .hv-particle--2 { top: 78%; left: 82%; animation: hv-drift 11s ease-in-out infinite reverse; opacity: 0.8; }
        .hv-particle--3 { top: 28%; left: 86%; width: 4px; height: 4px; animation: hv-drift 13s ease-in-out infinite; opacity: 0.7; }
        .hv-particle--4 { top: 82%; left: 18%; width: 4px; height: 4px; animation: hv-drift 8s ease-in-out infinite reverse; opacity: 0.6; }

        @keyframes hv-spin {
          from { transform: rotateX(var(--rx, 70deg)) rotateZ(0deg); }
          to   { transform: rotateX(var(--rx, 70deg)) rotateZ(360deg); }
        }
        .hv-ring--1 { --rx: 70deg; }
        .hv-ring--2 { --rx: 60deg; }
        .hv-ring--3 { --rx: 72deg; }

        @keyframes hv-breath {
          0%, 100% { transform: scale(1);   opacity: 0.95; }
          50%      { transform: scale(1.06); opacity: 1; }
        }
        @keyframes hv-float {
          0%, 100% { transform: translateY(0px); }
          50%      { transform: translateY(-8px); }
        }
        @keyframes hv-drift {
          0%, 100% { transform: translate(0, 0); }
          50%      { transform: translate(8px, -10px); }
        }

        @media (prefers-reduced-motion: reduce) {
          .hv-glow, .hv-ring, .hv-core, .hv-particle { animation: none !important; }
          .hero-visual .hv-glow,
          .hero-visual .hv-guide,
          .hero-visual .hv-ring,
          .hero-visual .hv-core,
          .hero-visual .hv-particle {
            opacity: 1 !important;
            transition: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default HeroVisual;
